import { CheckCircle, FileText, TriangleAlert } from "lucide-react";

import { Button } from "components/ui/Button";
import { HealthlinkXsltFrame } from "features/healthlink/components/HealthlinkXsltFrame/HealthlinkXsltFrame";
import { useSignOffMncmsInboundMessage } from "features/pregnancy/hooks/useSignOffMncmsInboundMessage";

import style from "./MncmsMessageDetail.module.css";

// The list payload carries everything we need (metadata + hl7MessageContent +
// xslContent), so the preview renders straight from the row — no per-id fetch.
export const MncmsMessageDetail = ({ message, direction, onSendCorrection }) => {
  const isInbound = direction === "inbound";
  const id = message?.id;

  const { mutate: signOff, isPending: isSigningOff } =
    useSignOffMncmsInboundMessage();

  const hasPreview = !!message?.hl7MessageContent && !!message?.xslContent;

  return (
    <div className={style.detail}>
      {!!message?.isCorrection && (
        <div className={style.correctionBanner}>
          <FileText size={20} aria-hidden />
          <div>
            <p className={style.correctionTitle}>Correction</p>
            <p className={style.correctionText}>
              This message is a correction to a previously sent record.
            </p>
          </div>
        </div>
      )}

      {!!message?.errorMessage && (
        <div className={style.errorBanner}>
          <TriangleAlert size={20} aria-hidden />
          <div>
            <p className={style.errorTitle}>Message not delivered</p>
            <p className={style.errorText}>{message.errorMessage}</p>
          </div>
        </div>
      )}

      {/* The preview's Print button portals in here (KIN-1979), so the two actions sit together
          instead of Print floating on a line of its own below. */}
      {isInbound && (
        <div className={style.toolbar} data-healthlink-toolbar-slot>
          {message?.signedOff ? (
            <span className={style.reviewed}>
              <CheckCircle size={16} aria-hidden />
              Reviewed
              {message.signedOffBy ? ` by ${message.signedOffBy}` : ""}
            </span>
          ) : (
            <Button
              variant="primary"
              startIcon={CheckCircle}
              disabled={isSigningOff || !id}
              onClick={() => signOff(id)}
            >
              {isSigningOff ? "Signing off…" : "Mark as reviewed"}
            </Button>
          )}
        </div>
      )}

      {hasPreview ? (
        <HealthlinkXsltFrame
          xml={message.hl7MessageContent}
          xsl={message.xslContent}
        />
      ) : (
        <div className={style.emptyState}>
          <p className={style.emptyStateTitle}>Preview unavailable</p>
          <p className={style.emptyStateText}>
            The message content (XML) or its stylesheet (XSL) isn&apos;t
            available yet, so it can&apos;t be rendered.
          </p>
        </div>
      )}

      {!isInbound && !!message?.anteNatalConsultationId && !!onSendCorrection && (
        <div className={style.footer}>
          <Button
            variant="primary"
            onClick={() => onSendCorrection(message.anteNatalConsultationId)}
          >
            Send correction
          </Button>
        </div>
      )}
    </div>
  );
};
